import Link from "next/link";
import ResponsiveImage from "../../atoms/image/ResponsiveImage";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../../styles/cssinjs/helpers/variables.js";

const getAllPages = (pages) => {
  const allSocialmediaPages = pages.featuredList["social media"];
  const allPaymentPages = pages.featuredList["payment"];
  const allWebsitePages = pages.featuredList["website booking"];
  const allCalendarSyncPages = pages.featuredList["calendar sync"];
  const allVideoMeetingPages = pages.featuredList["video meeting"];
  const allMarketingPages = pages.featuredList["marketing"];
  const allSalesPages = pages.featuredList["sales and crm"];
  const allBusinessPages = pages.featuredList["business"];
  const allPages = {
    allSocialmediaPages,
    allPaymentPages,
    allWebsitePages,
    allCalendarSyncPages,
    allVideoMeetingPages,
    allMarketingPages,
    allSalesPages,
    allBusinessPages,
  };
  return pages.featuredList;
};

const PagesList = ({ data, tab, wrapClassName }) => {
  let pagesForSelectedLanguage = "";
  if (tab === "all integrations") {
    pagesForSelectedLanguage = getAllPages(data);
  } else {
    pagesForSelectedLanguage = data["featuredList"][tab.toLowerCase()];
  }
  const { ctaOneContent } = data;
  const AllIntegrationSectionOne = () => {
    const appMode = process.env.APP_MODE;
    const link =
      ctaOneContent?.cta?.href?.[appMode] || ctaOneContent?.cta?.href;
    return (
      <section className="lg:pb-10 md:pb-1 md:pr-0 md:pl-0 bg-light-blue-grey img-wd-mob">
        <div className="container cta-contrast cta-contrast-white has-image has-img-offset br-8 lg:d-fx lg:mt-20 md:mt-4 md:mb-5 custom-height md:pb-0 lg:pb-10">
          <article className="lg:maxw-md lg:mr-5 md:mb-5">
            <h2 className="text-lg lg:maxw-xss md:mb-2">
              {ctaOneContent.title}
            </h2>
            <p className="lg:mt-3 lg:mb-5 md:mb-2">
              {ctaOneContent.description}
            </p>
            <a
              href={link}
              className="btn-primary btn-lg md:w-100"
              id="integrations-page-middle-cta"
            >
              <span>{ctaOneContent.cta.text}</span>
            </a>
          </article>
          <ResponsiveImage
            dpi={ctaOneContent.image.prefixes.dpi}
            mobile={ctaOneContent.image.prefixes.mobile}
            src={ctaOneContent.image.src}
            alt={ctaOneContent.image.alt}
            wrapClass="dp-block"
          />
        </div>
        <style jsx>
          {`
            section :global(.dp-block) {
              top: initial !important;
              bottom: 0;
            }
          `}
        </style>
      </section>
    );
  };
  const IntegrationsList = ({ id, order, data, classes }) => {
    let cardClasses;
    // lg:mt-5 for margin top
    // md:mt-2.5 only for first card
    if (data.columnCount === 4) {
      cardClasses = "has-four-col";
    }
    if (data.columnCount === 3) {
      cardClasses = "has-three-col";
    }
    const Lists = ({ data, cols }) => {
      let offsetForSecondRow = "";
      let offsetForFirstCard = "";

      return data.map((item, count) => {
        if (count === 1) {
          offsetForFirstCard = "md:mt-2.5";
        }
        return item.hasOwnProperty("isDummy") ? (
          <div
            key={count}
            style={{ visibility: "hidden", padding: "0px" }}
            data-usecase="Dummy element to align with the grid system"
          ></div>
        ) : (
          <Link
            href={item.href}
            key={count}
            prefetch={false}
            className={`hover-review-card ${offsetForFirstCard} ${offsetForSecondRow}`}
            id={item.id}
          >
            <figure dangerouslySetInnerHTML={{ __html: item.icon }}></figure>
            <div>
              <h3>{item.heading}</h3>
              <p>{item.desc}</p>
            </div>
            <style global jsx>{`
              #no-box-shadow {
                border: 1px solid ${colors.primitive.black[300]};
                box-shadow: none;
                background-color: transparent;
                align-items: center;
              }
              #no-box-shadow figure {
                display: none;
              }
              #no-box-shadow div {
                text-align: center;
                margin-right: auto;
                margin-left: auto;
              }
              #no-box-shadow h3 {
                font-size: ${font.size.md[1]};
                line-height: ${lineHeight[7]};
                font-weight: ${font.weight.normal};
                color: ${colors.derived.primary};
              }
              #no-box-shadow p {
                font-size: ${font.size.md[1]};
                line-height: ${lineHeight[7]};
                color: ${colors.derived.brand};
              }
            `}</style>
          </Link>
        );
      });
    };
    const ZapLists = ({ data, cols }) => {
      let offsetForSecondRow = "";
      let offsetForFirstCard = "";

      return data.map((item, count) => {
        if (count === 1) {
          offsetForFirstCard = "md:mt-2.5";
        }
        return item.hasOwnProperty("isDummy") ? (
          <div
            key={count}
            style={{ visibility: "hidden", padding: "0px" }}
            data-usecase="Dummy element to align with the grid system"
          ></div>
        ) : (
          <Link
            href={item.href}
            key={count}
            prefetch={false}
            className={`hover-review-card zap-card lg:mb-5 ${offsetForFirstCard} ${offsetForSecondRow}`}
            id={item.id}
          >
            <figure dangerouslySetInnerHTML={{ __html: item.icon }}></figure>
            <div>
              <h3>{item.heading}</h3>
            </div>
            <style jsx>{`
              .zap-card {
                display: flex;
                align-items: center;
              }
            `}</style>
          </Link>
        );
      });
    };
    const ViaZapierLine = () => {
      return (
        <div className="image-line lg:mt-7 lg:mb-7 md:mt-5 md:mb-5">
          <span className="text-xs text-light-grayish-blue">via Zapier</span>
          <style jsx>{`
            .image-line {
              position: relative;
              overflow: visible;
              height: 1px;
              border-collapse: collapse;
              background-color: ${colors.primitive.grey[1000]};
            }
            .image-line span {
              position: absolute;
              left: 50%;
              top: 50%;
              width: 200px;
              text-align: center;
              margin-left: -100px;
              margin-top: -15px;
              background-color: ${colors.primitive.grey[600]};
            }
          `}</style>
        </div>
      );
    };
    return (
      <section className={`bg-light-blue-grey img-wd-mob ${classes}`} id={id}>
        <div className="container">
          <article className="lg:maxw-lg mx-auto lg:text-center md:text-left">
            <h2 className="text-lg lg:mb-3 md:mb-2 md:pt-1">{data.heading}</h2>
            <p className="lg:mb-10 md:mb-5">{data.desc}</p>
          </article>
          <div
            className={`card--default card--showcase has-gutter-md c-padding ${cardClasses}`}
          >
            <Lists data={data.lists} cols={data.columnCount} />
          </div>
          {data.hasOwnProperty("zap") && (
            <>
              <ViaZapierLine />
              <div
                className={`card--default card--showcase has-gutter-md c-padding ${cardClasses}`}
              >
                <ZapLists data={data.zap} cols={data.zapColumnCount} />
              </div>
            </>
          )}
        </div>
      </section>
    );
  };
  const AllIntegrationList = () => {
    return (
      <>
        <IntegrationsList
          id="social-media"
          order="one"
          data={pagesForSelectedLanguage["social media"]}
          classes="lg:pt-10 lg:pb-10 md:pb-8 ng-m-top pcustom md:pt-10 md:pr-0 md:pl-0"
        />
        <IntegrationsList
          id="payment"
          order="two"
          data={pagesForSelectedLanguage["payment"]}
          classes="lg:pt-5 lg:pb-5 custom-bg-offset bg-offset md:pb-1 md:pr-0 md:pl-0"
        />
        <AllIntegrationSectionOne />
        <IntegrationsList
          id="website-and-CMS"
          order="three"
          data={pagesForSelectedLanguage["website booking"]}
          classes="lg:pt-10 lg:pb-10 md:pb-8 md:pr-0 md:pl-0"
        />
        <IntegrationsList
          id="calendars"
          order="four"
          data={pagesForSelectedLanguage["calendar sync"]}
          classes="lg:pt-5 lg:pb-10 md:pb-8 md:pr-0 md:pl-0"
        />
        <IntegrationsList
          id="video-meeting"
          order="five"
          data={pagesForSelectedLanguage["video meeting"]}
          classes="lg:pt-5 lg:pb-10 md:pb-8 md:pr-0 md:pl-0"
        />
        <IntegrationsList
          id="marketing"
          order="six"
          data={pagesForSelectedLanguage["marketing"]}
          classes="lg:pt-4 lg:pb-10 md:pb-8 md:pr-0 md:pl-0"
        />
        <IntegrationsList
          id="sales-and-CRM"
          order="seven"
          data={pagesForSelectedLanguage["sales and crm"]}
          classes="lg:pt-5 lg:pb-10 md:pb-8 md:pr-0 md:pl-0"
        />
        <IntegrationsList
          id="business"
          order="eight"
          data={pagesForSelectedLanguage["business"]}
          classes="root-integrations lg:pt-5 lg:pb-10 md:pb-5 bg-offset md:pr-0 md:pl-0"
        />
      </>
    );
  };

  return (
    <section
      className={`bg-light-blue-grey lg:-mt-10 lg:pt-20 md:pt-7 ${wrapClassName}`}
      id="default-container"
    >
      {tab === "all integrations" ? (
        <AllIntegrationList />
      ) : (
        <IntegrationsList
          order="one"
          data={pagesForSelectedLanguage}
          classes="lg:pt-10 lg:pb-10 md:pb-8 ng-m-top pcustom md:pt-10 md:pr-0 md:pl-0"
        />
      )}
    </section>
  );
};

export default PagesList;
